import {
  IEmojiContentBlock,
  IEvent,
  IRichTextContentBlock,
  ISportEventContentBlock,
  IWebEmbedContentBlock
} from "@webng-types/write-model";
import {parseHTML} from '@webng/isomorphic-dom'
import {OEmbed} from "../webembed/OEmbed";

export function getSportTiming(event: IEvent) {

  if(event.sport_event && (event.sport_event.time_of_play || event.sport_event.phase?.name)) {
    return {
      phase: event.sport_event.phase,
      timeOfPlay: event.sport_event.time_of_play
    }
  } else {
    return undefined
  }
}

function getTextContent(el: Element): string[] {
  let text: string[] = []
  el.childNodes.forEach((value, key) => {
    if(value.nodeType === 1) {
      text = text.concat(getTextContent(value as Element))
    } else if(value.nodeType === 3) {
      text.push((value as Text).wholeText)
    }
  })
  return text;
}

function getHighlightPostTextForHTMLString(text: string): string {
  const {element} = parseHTML(text)

  const headlines = element.querySelectorAll("h1,h2,h3,h4,h5,h6")
  if (headlines.length > 0) {
    return getTextContent(headlines[0]).join(" ")
  } else {
    return getTextContent(element).join(" ")
  }
}


function getHighlightPostTextForRichTextContentBlock(richTextBlock: IRichTextContentBlock): string {
  return getHighlightPostTextForHTMLString(richTextBlock.text)
}

function getHighlightPostTextForSportEventContentBlock(sportEvent: ISportEventContentBlock): string {
  let highlightTextContent: string = ""

  if(sportEvent.title) {
    highlightTextContent = sportEvent.title
    if(sportEvent.team?.name) {
      highlightTextContent += ` ${sportEvent.team?.name}`
    }
    const players = sportEvent.players?.map(player => {
      if(player.player) {
        return `${player.event_player_title}: ${player.player.name}`
      } else {
        return undefined
      }
    }).filter(player => !!player) as string[]|undefined

    if(players && players.length > 0) {
      highlightTextContent += ` (${players.join(", ")})`
    }
  }

  return highlightTextContent
}

function getHighlightPostTextForEmojiContentBlock(emojiContentBlock: IEmojiContentBlock): string {
  const parts: string[] = []
  if(emojiContentBlock.emoji) parts.push(emojiContentBlock.emoji)
  if(emojiContentBlock.title) parts.push(getHighlightPostTextForHTMLString(emojiContentBlock.title))

  return parts.join(" ").trim()
}

function getHighlightPostTextForWebEmbedContentBlock(block: IWebEmbedContentBlock): string {
  if(block.oembed_json) {
    try {
      const oembed = JSON.parse(block.oembed_json) as OEmbed
      return oembed.title || ""
    } catch(e) {
      return ""
    }
  } else {
    return ""
  }
}

export function getHighlightPostText(event: IEvent): string {
  const candiates: {priority: number, index: number, text: string}[] = []
  const contents = event.contents || []

  for(let i = 0; i < contents.length; i++) {
    const block = contents[i]

    switch(block._type) {
      case "Tik::Model::Content::RichTextContentBlock":
        // directly jump out as rich text content block is the most important one
        return getHighlightPostTextForRichTextContentBlock(block as IRichTextContentBlock)
      case "Tik::Model::Content::SportEventContentBlock":
        candiates.push({priority: 2, index: i, text: getHighlightPostTextForSportEventContentBlock(block as ISportEventContentBlock)})
        break;
      case "Tik::Model::Content::EmojiContentBlock":
        candiates.push({priority: 3, index: i, text: getHighlightPostTextForEmojiContentBlock(block as IEmojiContentBlock)})
        break;
      case "Tik::Model::Content::QuoteContentBlock":
        candiates.push({priority: 4, index: i, text: getHighlightPostTextForHTMLString(block.text)})
        break;
      case "Tik::Model::Content::WebEmbedContentBlock":
        candiates.push({priority: 5, index: i, text: getHighlightPostTextForWebEmbedContentBlock(block)})
        break;
      case "Tik::Model::Content::PollContentBlock":
        candiates.push({priority: 6, index: i, text: block.question || ""})
        break;
      case "Tik::Model::Content::PlayerCardContentBlock":
        candiates.push({priority: 7, index: i, text: block.player?.name || ""})
        break;
    }
  }

  if(event.sport_event) {
    candiates.push({priority: 1, index: 0, text: getHighlightPostTextForSportEventContentBlock(event.sport_event)})
  }

  const sortedCandidates = candiates
    .filter(c => c.text !== "")
    .sort((a, b) => a.priority - b.priority || a.index - b.index)

  if(sortedCandidates.length > 0) {
    return sortedCandidates[0].text
  } else {
    return ""
  }
}

export function getHeadlineFromEvent(event: IEvent){
  let headline: string|null = null

  let richTextBlock = event.contents?.find(content => content._type === "Tik::Model::Content::RichTextContentBlock");
  if(richTextBlock){
    richTextBlock = richTextBlock as IRichTextContentBlock;

    const {element} = parseHTML(richTextBlock.text)

    const headlines = element.querySelectorAll("h1,h2,h3,h4,h5,h6")
    if(headlines.length > 0) {
      headline = getTextContent(headlines[0]).join(" ")
    }
  }

  if(!headline && event.sport_event) {
    headline = event.sport_event.title || null
  }

  return headline
}
